import { useCallback, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { useHistory } from 'hooks/useHistory'
import {
  UPDATE_QUESTION_PASSAGE,
  GET_ONE_QUESTION_PASSAGE,
  GET_QUESTION_PASSAGE_LOCATIONS,
} from 'queries/questionPassage'
import { CreateQuestionPassageInput } from '__generatedTypes__/globalTypes'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { updateCARSQuestion, updateCARSQuestionVariables } from '__generatedTypes__/updateCARSQuestion'
import {
  findOneQuestionPassage,
  findOneQuestionPassageVariables,
  findOneQuestionPassage_findOneQuestionPassage as QuestionPassage,
} from '__generatedTypes__/findOneQuestionPassage'
import {
  findQuestionPassageLocation,
  findQuestionPassageLocationVariables,
} from '__generatedTypes__/findQuestionPassageLocation'
import { Form } from '../Form'

interface IProps {
  questionEditId?: string
  onSubmit?: (question?: QuestionPassage) => void
  disableDeleteButton?: boolean
}

export const Edit: FC<IProps> = ({ questionEditId: questionIdProp, onSubmit, disableDeleteButton = false }) => {
  const { questionPassageId: questionPassageIdParam = '' } = useParams()
  const questionId = questionIdProp ?? questionPassageIdParam
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [update] = useMutation<updateCARSQuestion, updateCARSQuestionVariables>(UPDATE_QUESTION_PASSAGE)
  const { data, loading, refetch } = useQuery<findOneQuestionPassage, findOneQuestionPassageVariables>(
    GET_ONE_QUESTION_PASSAGE,
    {
      variables: {
        params: questionId,
      },
      fetchPolicy: 'network-only',
    }
  )
  const locationsState = useQuery<findQuestionPassageLocation, findQuestionPassageLocationVariables>(
    GET_QUESTION_PASSAGE_LOCATIONS,
    {
      variables: {
        id: questionId,
      },
      fetchPolicy: 'network-only',
    }
  )
  const question = data?.findOneQuestionPassage
  const questionLocations = locationsState.data?.findQuestionPassageLocation

  const handleSubmit = useCallback(
    async (question: CreateQuestionPassageInput) => {
      try {
        await update({
          variables: {
            params: {
              passage: {
                id: questionId,
                isTemplate: question.isTemplate ?? false,
                templateName: question.templateName,
                content: question.content,
                subject: question.subject,
                level: question.level,
                title: question.title,
                history: populateHistoryWithUser(question.history),
              },
              questions: question.questions || [],
            },
          },
        })

        await refetch().then(({ data }) => onSubmit?.(data.findOneQuestionPassage ?? undefined))

        toast.success('Question updated successfully')
      } catch (error) {
        toast.error('Failed to update question')
      }
    },
    [questionId, populateHistoryWithUser, update, refetch, onSubmit]
  )

  if (loading || locationsState.loading || loadingHistory) {
    return <PageLoader />
  }

  if (!question) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Question not found</h1>
        </div>
      </div>
    )
  }

  return (
    <Form
      passageQuestion={question}
      passageQuestionLocations={questionLocations ?? undefined}
      onSubmit={handleSubmit}
      disableDeleteButton={disableDeleteButton}
    />
  )
}
